import type { RegionOption } from '../../../../shared/regionAccessOptions';
import { regionChoicesFor, regionsAfterRoleChange } from '../director-regions';
import type { UserRoleId } from '../roleTaxonomy';
import type { UserRow } from '../userViewModel';

import ImportedNameMatches from './ImportedNameMatches';
import type { InviteFormState } from './invite-form-model';
import InviteEmailField, { type EmailMatch } from './InviteEmailField';
import InviteFormField from './InviteFormField';
import InviteLinkTargetCard from './InviteLinkTargetCard';
import InviteNameFields from './InviteNameFields';
import InviteNotice from './InviteNotice';
import InviteRegionField from './InviteRegionField';
import RoleCheckboxGroup from './RoleCheckboxGroup';

interface InviteDrawerBodyProps {
  form: InviteFormState;
  setForm: React.Dispatch<React.SetStateAction<InviteFormState>>;
  match: EmailMatch;
  /** Imported rows whose name matches the typed one (net-new email only). */
  nameMatches: UserRow[];
  onPickImported: (user: UserRow) => void;
  /** Present when the link target was picked from `nameMatches`. */
  onClearPicked?: () => void;
  grantableRoleIds: UserRoleId[];
  /** Regions the viewer may assign. */
  regionOptions: RegionOption[];
  requiresRegion: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  /** Set while the operator is being asked to link the email's existing login. */
  linkNotice: string | null;
}

/** The scrolling form between the drawer's header and footer. */
export default function InviteDrawerBody({
  form,
  setForm,
  match,
  nameMatches,
  onPickImported,
  onClearPicked,
  grantableRoleIds,
  regionOptions,
  requiresRegion,
  errorMessage,
  successMessage,
  linkNotice,
}: InviteDrawerBodyProps) {
  const linkedUserId = match.kind === 'backfill' ? match.user.id : null;
  function handleRolesChange(ids: UserRoleId[]): void {
    setForm((f) => ({
      ...f,
      roleIds: ids,
      regionIds: regionsAfterRoleChange(ids, f.regionIds, regionOptions, linkedUserId),
    }));
  }

  return (
    <div className="flex-1 overflow-auto px-6 py-4 flex flex-col gap-4">
      <InviteEmailField
        email={form.email}
        onChange={(v) => setForm((f) => ({ ...f, email: v }))}
        match={match}
      />
      {match.kind === 'backfill' && (
        <InviteLinkTargetCard user={match.user} onClear={onClearPicked} />
      )}
      <InviteNameFields
        firstName={form.firstName}
        lastName={form.lastName}
        onFirstNameChange={(v) => setForm((f) => ({ ...f, firstName: v }))}
        onLastNameChange={(v) => setForm((f) => ({ ...f, lastName: v }))}
      />
      {match.kind === 'none' && (
        <ImportedNameMatches candidates={nameMatches} onPick={onPickImported} />
      )}
      <InviteFormField label="Roles" required hint="Pick one or more.">
        <RoleCheckboxGroup
          value={form.roleIds}
          onChange={handleRolesChange}
          grantableIds={grantableRoleIds}
        />
      </InviteFormField>
      {requiresRegion && (
        <InviteRegionField
          roleIds={form.roleIds}
          options={regionChoicesFor(form.roleIds, regionOptions, linkedUserId)}
          selectedIds={form.regionIds}
          onChange={(ids) => setForm((f) => ({ ...f, regionIds: ids }))}
        />
      )}
      {linkNotice && !successMessage && <InviteNotice tone="warning" message={linkNotice} />}
      {successMessage && <InviteNotice tone="success" message={successMessage} />}
      {errorMessage && <InviteNotice tone="error" message={errorMessage} />}
    </div>
  );
}
